import {
  createContext,
  useEffect,
  useMemo,
  useState,
  useContext,
  type ReactNode,
} from "react";
import type { JSX } from "react";
import posthog from "posthog-js";
import { useAppRuntime } from "../providers/AppRuntimeProvider";
import { useRouterContext } from "../providers/RouterProvider";
import { useWorkspaceStore } from "../store/workspaceStore";
import {
  ANALYTICS_EVENT_VERSION,
  captureAnalyticsFlowContext,
  createAnalyticsClient,
  createDisabledAnalyticsFlowContext,
  type AnalyticsClient,
  type RuntimeAnalyticsConfig,
} from "./client";

interface ClientConfigResponse {
  readonly analytics: RuntimeAnalyticsConfig;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AnalyticsContext = createContext<AnalyticsClient | null>(null);
let appOpenedCaptured = false;
let onboardingAppLoadedCaptured = false;
let lastObservedWorkspaceId: string | null = null;

async function loadClientConfig(baseUrl: string): Promise<RuntimeAnalyticsConfig> {
  const response = await fetch(`${baseUrl}/api/client-config`);
  if (!response.ok) {
    throw new Error(`client config request failed: ${String(response.status)}`);
  }
  const body = (await response.json()) as ClientConfigResponse;
  return body.analytics;
}

function assertCompatibleAnalyticsSchema(config: RuntimeAnalyticsConfig): void {
  if (config.mode === "off") return;
  if (config.internalEventSchemaVersion !== ANALYTICS_EVENT_VERSION) {
    throw new Error("analytics event schema version is incompatible");
  }
}

function shouldCaptureAppOpened(): boolean {
  if (appOpenedCaptured) return false;
  appOpenedCaptured = true;
  return true;
}

function shouldCaptureOnboardingAppLoaded(): boolean {
  if (onboardingAppLoadedCaptured) return false;
  onboardingAppLoadedCaptured = true;
  return true;
}

function updateObservedWorkspaceId(workspaceId: string): string | null {
  const previous = lastObservedWorkspaceId;
  // @tested-by: tst_fe_analytics_011_workspace_switch_survives_app_boundary_remount
  // @invariant: workspace switch detection must survive the keyed App workspace remount.
  lastObservedWorkspaceId = workspaceId;
  return previous;
}

function reportAnalyticsError(error: unknown): void {
  // eslint-disable-next-line no-console
  console.error("analytics capture failed", error);
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAnalytics(): AnalyticsClient {
  const client = useContext(AnalyticsContext);
  if (!client) throw new Error("useAnalytics must be used inside AnalyticsProvider");
  return client;
}

export function AnalyticsProvider({ children }: { readonly children: ReactNode }): JSX.Element {
  const runtime = useAppRuntime();
  const [config, setConfig] = useState<RuntimeAnalyticsConfig | null>(null);
  const [configError, setConfigError] = useState<Error | null>(null);
  const [initializedPosthogKey, setInitializedPosthogKey] = useState<string | null>(null);
  const posthogConfigKey =
    config?.mode === "posthog"
      ? `${config.posthogProjectApiKey ?? ""}\n${config.posthogHost ?? ""}`
      : null;
  const flow = useMemo(() => {
    if (!config) return null;
    if (config.mode === "off") return createDisabledAnalyticsFlowContext();
    return captureAnalyticsFlowContext();
  }, [config]);

  useEffect(() => {
    let active = true;
    void loadClientConfig(runtime.transport.baseUrl)
      .then((next) => {
        if (active) {
          assertCompatibleAnalyticsSchema(next);
          setConfig(next);
        }
      })
      .catch((error: unknown) => {
        if (active) {
          setConfigError(
            error instanceof Error
              ? error
              : new Error("analytics client config failed"),
          );
        }
      });
    return (): void => {
      active = false;
    };
  }, [runtime.transport.baseUrl]);

  useEffect(() => {
    if (config?.mode !== "posthog") {
      setInitializedPosthogKey(null);
      return;
    }
    if (!config.posthogProjectApiKey || !config.posthogHost) {
      setConfigError(new Error("posthog analytics mode requires key and host"));
      setInitializedPosthogKey(null);
      return;
    }
    // @tested-by: tst_fe_analytics_002_posthog_mode_initializes_sdk_with_versions
    // @tested-by: tst_fe_analytics_017_posthog_init_precedes_initial_app_events
    posthog.init(config.posthogProjectApiKey, {
      api_host: config.posthogHost,
      capture_pageview: false,
      autocapture: false,
      disable_session_recording: true,
    });
    setInitializedPosthogKey(posthogConfigKey);
  }, [config, posthogConfigKey]);

  const client = useMemo(
    () => {
      if (!config || !flow) return null;
      if (config.mode === "posthog" && initializedPosthogKey !== posthogConfigKey) {
        return null;
      }
      return createAnalyticsClient({
        config,
        flow,
        internalTransport: runtime.transport,
        posthog: config.mode === "posthog" ? posthog : undefined,
      });
    },
    [config, flow, initializedPosthogKey, posthogConfigKey, runtime.transport],
  );

  if (configError) {
    throw configError;
  }
  if (!client) {
    return <div className="h-screen bg-surface-primary" />;
  }

  return (
    <AnalyticsContext.Provider value={client}>
      {children}
    </AnalyticsContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function resetAnalyticsAppEventStateForTests(): void {
  appOpenedCaptured = false;
  onboardingAppLoadedCaptured = false;
  lastObservedWorkspaceId = null;
}

export function AnalyticsAppEvents(): JSX.Element | null {
  const analytics = useAnalytics();
  const router = useRouterContext();
  const selectedWorkspaceId = useWorkspaceStore((state) => state.selectedWorkspaceId);
  const workspaces = useWorkspaceStore((state) => state.workspaces);

  useEffect(() => {
    if (analytics.mode === "off" || !shouldCaptureAppOpened()) return;
    void analytics.capture(
      "app_opened",
      {
        entry_hash: window.location.hash,
      },
      { source: "app_boot" },
    ).catch(reportAnalyticsError);
  }, [analytics]);

  useEffect(() => {
    if (analytics.mode === "off" || !shouldCaptureOnboardingAppLoaded()) return;
    void analytics.capture(
      "onboarding_step_completed",
      {
        step_id: "app_loaded",
      },
      { source: "app_boot" },
    ).catch(reportAnalyticsError);
  }, [analytics]);

  useEffect(() => {
    if (analytics.mode === "off") return;
    void analytics.capture(
      "route_viewed",
      {
        has_entity: router.entityId != null,
        module_id: router.activeModuleId,
        ...(router.entityType ? { entity_type: router.entityType } : {}),
      },
      { source: "router" },
    ).catch(reportAnalyticsError);
  }, [analytics, router.activeModuleId, router.entityId, router.entityType]);

  useEffect(() => {
    if (analytics.mode === "off" || selectedWorkspaceId == null) return;
    const previous = updateObservedWorkspaceId(selectedWorkspaceId);
    if (previous == null) {
      return;
    }
    if (previous === selectedWorkspaceId) return;

    const fromWorkspace = workspaces.find((workspace) => workspace.id === previous);
    const toWorkspace = workspaces.find((workspace) => workspace.id === selectedWorkspaceId);
    if (!fromWorkspace || !toWorkspace) return;

    void analytics.capture(
      "workspace_switched",
      {
        from_workspace_kind: fromWorkspace.kind,
        to_workspace_kind: toWorkspace.kind,
        ...(toWorkspace.authMethod ? { to_workspace_auth_method: toWorkspace.authMethod } : {}),
      },
      { source: "workspace_store" },
    ).catch(reportAnalyticsError);
  }, [analytics, selectedWorkspaceId, workspaces]);

  return null;
}
